import React from 'react';
import Select from 'react-select';
import { FormGroup, Label } from 'reactstrap';

import 'style/layout/form/SelectInputFieldControlled.css';

interface IOption {
  label: string;
  value: string;
}

interface IGroup {
  label: string;
  options: IOption[];
}

const textOf = (children: React.ReactNode): string =>
  React.Children.toArray(children)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : ''
    )
    .join('');

// The <option> and <optgroup> elements of a native select as the choices of the search
const toChoices = (options: React.ReactNode): (IOption | IGroup)[] =>
  React.Children.toArray(options).flatMap((element) => {
    if (!React.isValidElement(element)) {
      return [];
    }

    const props = element.props as {
      children?: React.ReactNode;
      label?: string;
      value?: number | string;
    };
    if (element.type === 'optgroup') {
      return [
        {
          label: props.label ?? '',
          options: toChoices(props.children) as IOption[],
        },
      ];
    }

    if (React.Children.count(props.children) > 0 && element.type !== 'option') {
      return toChoices(props.children);
    }

    return [
      {
        label: textOf(props.children),
        value: props.value === undefined ? '' : String(props.value),
      },
    ];
  });

const flatten = (choices: (IOption | IGroup)[]): IOption[] =>
  choices.flatMap((choice) =>
    'options' in choice ? choice.options : [choice]
  );

/**
 * A dropdown that can be typed into to search its choices, without opening it first from
 * its arrow. Takes the same props as SelectInputFieldControlled (the options as <option>
 * elements) and answers its onChange with an event-like { target: { name, value } }.
 */
const SearchableSelectField = ({
  className,
  disabled,
  formGroupClassName,
  id,
  label,
  labelClassName,
  name,
  onChange,
  options,
  required,
  value,
}: {
  className?: string;
  disabled?: boolean;
  formGroupClassName?: string;
  id: string;
  label?: string;
  labelClassName?: string;
  name?: string;
  onChange: any;
  options: any;
  required?: boolean;
  value: any;
}): JSX.Element => {
  const choices = toChoices(options);
  const current = value === null || value === undefined ? '' : String(value);
  const selected =
    flatten(choices).find((choice) => choice.value === current) ?? null;

  const change = (choice: IOption | null) => {
    const target = { id, name: name ?? '', value: choice?.value ?? '' };
    onChange({ currentTarget: target, persist: () => undefined, target });
  };

  return (
    <FormGroup
      className={`select-input-field-controlled searchable-select-field ${
        formGroupClassName ?? ''
      }`}
    >
      {label === undefined ? null : (
        <Label className={`${labelClassName ?? ''} label`} for={id}>
          {label}
        </Label>
      )}
      <Select
        className={`${className ?? ''} text-field search-field`}
        classNamePrefix="searchable-select"
        inputId={id}
        isDisabled={disabled}
        name={name}
        noOptionsMessage={() => '–'}
        onChange={(choice) => change(choice as IOption | null)}
        options={choices}
        placeholder=""
        value={selected}
      />
      {/* Keeps the check of the browser for a required field */}
      {required ? (
        <input
          aria-hidden
          className="searchable-select-required"
          onChange={() => undefined}
          required
          tabIndex={-1}
          value={current}
        />
      ) : null}
    </FormGroup>
  );
};

export default SearchableSelectField;
